import { motion } from "framer-motion";
import { GraduationCap, FileSearch, Landmark, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    num: "01",
    icon: GraduationCap,
    title: "Score Education & Coaching",
    tag: "Understand the system",
    copy: "Private coaching that turns the scoring models from mystery into method. Learn exactly how every payment, balance, and inquiry moves your number.",
    points: ["One-on-one coaching sessions", "Score mechanics masterclass", "Habit systems that compound"],
    span: "lg:col-span-7",
  },
  {
    num: "02",
    icon: FileSearch,
    title: "Credit Analysis & Action Plans",
    tag: "Decode your report",
    copy: "A forensic, line-by-line review of your credit reports, translated into plain language and a sequenced, personal action plan.",
    points: ["Full three-bureau report review", "Prioritized 90-day roadmap", "Progress checkpoints"],
    span: "lg:col-span-5",
  },
  {
    num: "03",
    icon: Landmark,
    title: "Business Credit & Funding Guidance",
    tag: "Build beyond personal",
    copy: "Establish business credit from zero and position your company for funding — structured, credible, and lender-ready.",
    points: ["Business credit foundation", "Funding readiness assessment", "Lender preparation coaching"],
    span: "lg:col-span-12",
    wide: true,
  },
];

const Services = () => (
  <section id="services" className="py-28 lg:py-40 bg-ink-base" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <SectionHeading
          overline="The Practice"
          title={<>Three disciplines.<br />One direction — <span className="italic text-gold">up.</span></>}
          testId="services-heading"
        />
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 max-w-[220px] lg:text-right"
        >
          Every engagement begins with education, never with a sales pitch
        </motion.p>
      </div>

      <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.num}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
            className={`group relative bg-ink-surface border border-white/10 p-8 lg:p-12 hover:border-gold/40 transition-colors duration-500 ${s.span} ${
              s.wide ? "lg:flex lg:items-center lg:gap-16" : ""
            }`}
            data-testid={`service-card-${s.num}`}
          >
            <span className="absolute top-6 right-8 font-mono text-xs text-neutral-600 group-hover:text-gold transition-colors duration-500">
              {s.num}
            </span>
            <div className={s.wide ? "lg:flex-1" : ""}>
              <s.icon className="w-8 h-8 text-gold" strokeWidth={1.25} />
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{s.tag}</p>
              <h3 className="mt-3 font-serif text-2xl lg:text-3xl text-white tracking-tight">{s.title}</h3>
              <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed max-w-lg">{s.copy}</p>
            </div>
            <ul className={`mt-8 space-y-3 ${s.wide ? "lg:mt-0 lg:flex-1 lg:border-l lg:border-white/10 lg:pl-12" : ""}`}>
              {s.points.map((pt) => (
                <li key={pt} className="flex items-center gap-3 text-sm text-neutral-300 font-light">
                  <span className="w-1.5 h-1.5 rotate-45 bg-gold/70 shrink-0" />
                  {pt}
                </li>
              ))}
              <li className="pt-4">
                <button
                  onClick={() => scrollToSection("contact")}
                  className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:text-white transition-colors duration-300"
                  data-testid={`service-cta-${s.num}`}
                >
                  Start here
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </li>
            </ul>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
